import {
  Avatar,
  Button,
  DatePicker,
  Form,
  Input,
  Radio,
  Select,
  TimePicker,
  Row,
  Col,
  Space,
  notification,
  Typography,
} from "antd";
import LoginForm from "../LoginForm";
import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCaregiverSchedule } from "../../api/Caregivers";
import { useSessionMutation } from "../../api/Sessions";
import { config } from "../../config";
import { AuthContext } from "../../useAuth";
import { availabilityRangeOptions, splitTime } from "../../utils/timeUtils";
import CalendarButton from "../CalendarButton";
import SubmitButton from "../SubmitButton";

const { useForm } = Form;

const CaregiverSchedule = ({ caregiver, onClose }) => {
  const { authenticated } = useContext(AuthContext);
  return !authenticated ? (
    <>
      <Typography.Paragraph>
        Login or sign up to schedule a session
      </Typography.Paragraph>
      <LoginForm />
    </>
  ) : (
    <DisplaySchedule caregiver={caregiver} onClose={onClose} />
  );
};

const DisplaySchedule = ({ caregiver, onClose }) => {
  const [caregiverId, setCaregiverId] = useState();
  const [scheduledSession, setScheduledSession] = useState();
  const [availableDays, setAvailableDays] = useState([]);
  const [schedule, setSchedule] = useState();
  const [availableTimes, setAvailableTimes] = useState([]);
  const [disabled, setDisabled] = useState(true);
  const [sessionCost, setSessionCost] = useState();
  const [submitting, setSubmitting] = useState(false);
  const { user } = useContext(AuthContext);
  const session = useSessionMutation();

  const { data: caregiverSchedule } = useCaregiverSchedule(
    caregiverId,
    user?.Timezone
  );

  useEffect(() => {
    setCaregiverId(caregiver?.UserId);
  }, []);

  useEffect(() => {
    if (caregiverSchedule?.CaregiverSchedule) {
      let available = [];
      let sch = {};
      caregiverSchedule.CaregiverSchedule.forEach((s) => {
        available.push(s.date);
        sch[s.date] = s.ranges[0];
      });
      setAvailableDays(available);
      setSchedule(sch);
    }
  }, [caregiverSchedule]);

  function disabledDate(current) {
    return !availableDays.includes(current.format("YYYY-MM-DD"));
  }

  const submit = (values) => {
    setSubmitting(true);
    const sessionTime = splitTime(values.Time);
    const sessionDate = dayjs(values.Day)
      .hour(sessionTime.hour)
      .minute(sessionTime.minute)
      .second(0)
      .millisecond(0);

    const payload = {
      CaregiverId: caregiver.UserId,
      UserId: user.UserId,
      Rate: caregiver.Rate,
      CareType: caregiver.CareType,
      Category: values.Category,
      StartTime: sessionDate.toISOString(),
      SessionLength: values.Duration,
      EndTime: sessionDate.add(values.Duration, "minutes").toISOString(),
    };

    session
      .mutateAsync(payload)
      .then((r) => {
        setScheduledSession(r.Session.session);
      })
      .catch((error) => {
        notification.error({
          message: "Something went wrong",
          description: error.message,
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const cancel = () => {
    form.resetFields();
    onClose && onClose();
  };

  const selectDate = (date) => {
    let d = date.format("YYYY-MM-DD");
    // Find the range for the date and set the dropdown options
    let t = schedule[d];
    let times = availabilityRangeOptions(t.Start, t.End);
    let avt = times.map((t) => ({ label: t.regular, value: t.military }));
    setAvailableTimes(avt);
  };

  const [form] = useForm();

  const handleFormChange = (field) => {
    const { Day, Duration, Time, Category } = form.getFieldsValue();
    if (Duration) setSessionCost(Duration * (caregiver.Rate / 60));
    const hasErrors = !Day || !Duration || !Time || !Category;
    setDisabled(hasErrors);
  };

  return (
    <>
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <Avatar
          src={caregiver?.Avatar && `${config.imageUrl}/${caregiver?.Avatar}`}
          icon={caregiver?.FirstName?.substring(0, 1)}
          shape="square"
          size={100}
        />
      </div>
      {scheduledSession ? (
        <SessionInfo
          session={scheduledSession}
          caregiverName={caregiver.FirstName}
        />
      ) : (
        <Form
          onFieldsChange={handleFormChange}
          form={form}
          onFinish={submit}
          layout="vertical"
          style={{
            border: "1px solid #dedede",
            padding: "10px",
          }}
        >
          <Row gutter={10}>
            <Col span={12}>
              <Form.Item name="Day" rules={[{ required: true }]} label="Day">
                <DatePicker
                  onChange={selectDate}
                  showNow={false}
                  style={{ width: "100%" }}
                  format={"MMM Do"}
                  disabledDate={disabledDate}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="Time" rules={[{ required: true }]} label="Start">
                <Select options={availableTimes} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="Duration"
            rules={[{ required: true }]}
            label="Duration"
          >
            <Radio.Group buttonStyle="solid">
              <Radio.Button value="30">30 min</Radio.Button>
              <Radio.Button value="60">60 min</Radio.Button>
              <Radio.Button value="90">90 min</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="Category"
            rules={[{ required: true }]}
            label="Category"
          >
            <Select
              placeholder="Select category"
              options={caregiver?.Categories?.map((c) => {
                return { label: c, value: c };
              })}
            />
          </Form.Item>
          <Form.Item name="Notes" label="Notes (optional)">
            <Input.TextArea
              rows={5}
              placeholder="Comments, requests, or special instructions? Let your caregiver know here!"
            />
          </Form.Item>
          <Form.Item shouldUpdate>
            <Space direction="vertical" style={{ width: "100%" }}>
              <SubmitButton
                block
                type="primary"
                disabled={disabled}
                submitting={submitting}
              >
                Schedule {sessionCost && ` - $${sessionCost}`}
              </SubmitButton>
              <Button type="text" block onClick={() => cancel()}>
                cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      )}
    </>
  );
};

const SessionInfo = ({ session, caregiverName }) => {
  const navigate = useNavigate();
  return (
    <div style={{ textAlign: "center" }}>
      <Typography.Title level={4}>
        Session Scheduled with {caregiverName}!
      </Typography.Title>
      <Typography.Paragraph>
        What's next? You will be notified if {caregiverName} accepts the
        invitation for this session. If the session is not accepted by{" "}
        {caregiverName} you will not be charged.
      </Typography.Paragraph>
      <Space direction="vertical">
        <CalendarButton
          display={dayjs(session.StartTime).format("MMM Do h:mma")}
          title={"Session"}
          description={""}
          start={session.StartTime}
          end={session.EndTime}
        />
        <Button size="small" type="text" onClick={() => navigate("/sessions")}>
          View Sessions
        </Button>
      </Space>
    </div>
  );
};

export default CaregiverSchedule;
